import type { SerializedMeterAddon, SerializedMeterModel, UpdateMeterModelDto } from '@enmon/commons/lib/tools';
import { useServices } from 'containers/services';
import { useAppNotifications } from 'hooks';
import { useMutation, type UseMutationResult, useQueryClient } from 'react-query';
import { getErrorMessage } from 'utils/getErrorMessage';
import { MUTATION_KEYS, QUERY_KEYS } from 'utils/queryKeys';

type AssignModelToModuleArg = {
  model: SerializedMeterModel;
  moduleId: SerializedMeterAddon['id'];
  assign: boolean;
};

type AssignModelToModuleResult = SerializedMeterModel;

export const useAssignModelToModule = (): UseMutationResult<
  AssignModelToModuleResult,
  unknown,
  AssignModelToModuleArg
> => {
  const queryClient = useQueryClient();
  const { meterModelsApiClient } = useServices();
  const notify = useAppNotifications();

  return useMutation({
    mutationKey: MUTATION_KEYS.meterModels.assignToModule(),
    mutationFn: async ({ moduleId, model, assign }: AssignModelToModuleArg) => {
      const currentModuleIds = model.meter_addons?.map(({ id }) => id) ?? [];
      const isAssigned = currentModuleIds.includes(moduleId);

      if (assign === isAssigned) {
        return model;
      }

      const updatedModuleIds = assign
        ? [...currentModuleIds, moduleId]
        : currentModuleIds.filter(id => id !== moduleId);

      const dtoValues = {
        meter_addons: updatedModuleIds,
        commodity: model.commodity,
      } satisfies UpdateMeterModelDto;

      return await meterModelsApiClient.update({ id: model.id, values: dtoValues });
    },
    onError: (error, { assign }) => {
      notify.error({
        message: `There was an error while ${assign ? 'assigning' : 'unassigning'} model to meter module`,
        description: getErrorMessage(error),
      });
    },
    onSuccess: (_, { model, assign }) => {
      queryClient.invalidateQueries(QUERY_KEYS.meterModules.all());
      queryClient.invalidateQueries(QUERY_KEYS.meterModels.one(model.id));
      notify.success({
        message: `Model ${assign ? 'assigned to' : 'unassigned from'} meter module successfully`,
      });
    },
  });
};
